import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Star as StarIcon,
  Visibility as VisibilityIcon,
  SentimentVerySatisfied as PositiveIcon,
  SentimentSatisfied as NeutralIcon,
  SentimentVeryDissatisfied as NegativeIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import {
  colors,
  getOperationalText,
  operationalComponentSurfaces,
  operationalRadii
} from '../../../design-system/tokens';
import { QUALITATIVE_AI_ACCENT } from '../../../utils/aiAnalysisStatus';

import IconButtonWithTooltip from '../IconButtonWithTooltip';
import PlatformIcon from '../../atoms/PlatformIcon';

/**
 * OverlayIcons - Componente molecular para Ã­cones sobrepostos em cards de artigos
 * 
 * Este componente segue o princÃ­pio do Atomic Design como uma "molÃ©cula",
 * combinando vÃ¡rios Ã¡tomos (IconButtonWithTooltip) para mostrar indicadores
 * visuais do estado do artigo (favorito, lido, sentimento).
 */
const OverlayIcons = ({
  isFavorite,
  isRead,
  sentiment,
  mediaType,
  qualitativeAnalysisApplied = false,
  onToggleFavorite,
  onToggleRead,
  onUpdateSentiment,
  size = 'medium',
  iconSize = 22
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const platformBadgeHeight = 30;
  const statusIconSize = Math.max(16, iconSize - 4);
  const statusButtonSize = 26;
  const textTone = getOperationalText(isDarkMode);
  const iconTone = isDarkMode ? 'rgba(248, 250, 252, 0.88)' : textTone.mutedStrong;
  const iconToneHover = textTone.primary;
  const overlaySurface = operationalComponentSurfaces.stage;
  const overlaySurfaceHover = isDarkMode ? 'rgba(42, 45, 49, 0.98)' : '#ffffff';

  // FunÃ§Ã£o para obter o Ã­cone de sentimento apropriado
  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return <PositiveIcon sx={{ color: colors.sentiment.positive.main, fontSize: statusIconSize }} />;
      case 'negative':
        return <NegativeIcon sx={{ color: colors.sentiment.negative.main, fontSize: statusIconSize }} />;
      default:
        return <NeutralIcon sx={{ color: colors.sentiment.neutral.main, fontSize: statusIconSize }} />;
    }
  };

  // FunÃ§Ã£o para obter o nome de exibiÃ§Ã£o da plataforma
  const getPlatformDisplayName = (mediaType) => {
    switch (mediaType) {
      case 'YouTube':
        return 'YouTube';
      case 'Twitter':
        return 'X (Twitter)';
      case 'Instagram':
        return 'Instagram';
      case 'Threads':
        return 'Threads';
      case 'Facebook':
        return 'Facebook';
      case 'TikTok':
        return 'TikTok';
      case 'LinkedIn':
        return 'LinkedIn';
      case 'Pinterest':
        return 'Pinterest';
      case 'Kwai':
        return 'Kwai';
      case 'Online':
        return 'Online';
      // MÃ­dias Impressas
      case 'Jornal':
        return 'Jornal';
      case 'Revista':
        return 'Revista';
      // MÃ­dias EletrÃ´nicas
      case 'TV':
        return 'TV';
      case 'Radio':
        return 'RÃ¡dio';
      case 'Podcast':
        return 'Podcast';
      default:
        return mediaType || 'Artigo';
    }
  };

  // Estilo base para os botÃµes de Ã­cone
  const buttonStyle = {
    color: iconTone,
    width: statusButtonSize,
    height: statusButtonSize,
    minWidth: statusButtonSize,
    fontSize: statusIconSize,
    background: overlaySurface,
    border: 0,
    borderRadius: `${operationalRadii.pill}px`,
    boxShadow: 'none',
    backdropFilter: 'none',
    p: 0.15,
    m: 0,
    transition: 'background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease',
    '&:hover': {
      color: iconToneHover,
      background: overlaySurfaceHover,
      background: overlaySurfaceHover
    },
    '& .MuiSvgIcon-root': {
      color: 'currentColor'
    }
  };

  return (
    <>
      {/* Plataforma - Lado esquerdo */}
      {mediaType && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title={getPlatformDisplayName(mediaType)} arrow>
            <Box
            sx={{
                height: platformBadgeHeight,
                maxWidth: 142,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.65,
                px: 1.15,
                borderRadius: `${operationalRadii.pill}px`,
                color: iconTone,
                bgcolor: overlaySurface,
                border: 0,
                boxShadow: 'none',
                backdropFilter: 'none',
                userSelect: 'none',
                transition: 'color 0.16s ease, background-color 0.16s ease, border-color 0.16s ease',
                '&:hover': {
                  color: iconToneHover,
                  bgcolor: overlaySurfaceHover
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 15
                }
              }}
            >
              <Box sx={{ display: 'grid', placeItems: 'center', flexShrink: 0, lineHeight: 0 }}>
                <PlatformIcon mediaType={mediaType} size={15} color="currentColor" />
              </Box>
              <Typography
                component="span"
                translate="no"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'currentColor',
                  fontSize: '0.76rem',
                  lineHeight: 1,
                  fontWeight: 850,
                  letterSpacing: 0
                }}
              >
                {getPlatformDisplayName(mediaType)}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Ãcones de status - Lado direito */}
      <Box
        sx={{
            position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          flexDirection: 'row',
          gap: 0.45,
          zIndex: 2,
          background: 'none',
          boxShadow: 'none',
          p: 0,
          alignItems: 'center',
          minHeight: 0,
          minWidth: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ãcone de favorito (sÃ³ exibido se for favorito) */}
        {isFavorite && (
          <IconButtonWithTooltip
            title="Favorito"
            icon={<StarIcon sx={{ color: 'currentColor', fontSize: statusIconSize }} />}
            onClick={onToggleFavorite}
            size={size}
            sx={buttonStyle}
          />
        )}

        {/* Ãcone de lido (sÃ³ exibido se for lido) */}
        {isRead && (
          <IconButtonWithTooltip
            title="Lido"
            icon={<VisibilityIcon sx={{ color: 'currentColor', fontSize: statusIconSize }} />}
            onClick={onToggleRead}
            size={size}
            sx={buttonStyle}
          />
        )}

        {/* Ãcone de sentimento (sempre exibido) */}
        <IconButtonWithTooltip
          title="Sentimento"
          icon={getSentimentIcon(sentiment)}
          onClick={onUpdateSentiment}
          size={size}
          sx={buttonStyle}
        />

        {qualitativeAnalysisApplied && (
          <IconButtonWithTooltip
            title="Analise por IA aplicada"
            icon={<AutoAwesomeIcon sx={{ color: QUALITATIVE_AI_ACCENT, fontSize: statusIconSize }} />}
            onClick={(e) => e.stopPropagation()}
            size={size}
            sx={buttonStyle}
          />
        )}
      </Box>
    </>
  );
};

OverlayIcons.propTypes = {
  /** Se o artigo estÃ¡ favoritado */
  isFavorite: PropTypes.bool,

  /** Se o artigo foi lido */
  isRead: PropTypes.bool,

  /** Sentimento associado ao artigo */
  sentiment: PropTypes.oneOf(['positive', 'neutral', 'negative']),

  /** Tipo de mÃ­dia da plataforma */
  mediaType: PropTypes.string,
  qualitativeAnalysisApplied: PropTypes.bool,

  /** FunÃ§Ã£o para alternar o estado de favorito */
  onToggleFavorite: PropTypes.func,

  /** FunÃ§Ã£o para alternar o estado de lido */
  onToggleRead: PropTypes.func,

  /** FunÃ§Ã£o para atualizar o sentimento */
  onUpdateSentiment: PropTypes.func,

  /** Tamanho dos botÃµes */
  size: PropTypes.oneOf(['small', 'medium']),

  /** Tamanho dos Ã­cones */
  iconSize: PropTypes.number
};

export default OverlayIcons;
